import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { Agent } from "@/api/generated";
import { AgentProfile } from "@/components/offer/AgentProfile";
import { PLANNER_STRENGTHS_LABEL, PLANNER_SUPPORTED_TYPES_LABEL } from "@/constants/plannerProfile";
import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";
import { buildAgentName } from "@/utilities/agentName";
import { buildSupportedPropertyTypeLabels } from "@/utilities/agentSupportedPropertyTypes";

import NoImage from "@assets/image/NoImage.svg";

import { Chevron } from "../Chevron";
import { CertifiedPlannerBadge } from "../planner_onboarding/CertifiedPlannerBadge";

import { AgentBeginnerMessage } from "./AgentBeginnerMessage";
import { PropertyTypeTags, StrengthTags } from "./PlannerTags";

const useStyles = makeStyles(() => ({
  hero: {
    padding: "20px 24px 24px",
    backgroundColor: theme.palette.semantic["surface/subtle"],
    display: "flex",
    flexDirection: "column",
    gap: 12,
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: { borderRadius: 16, backgroundColor: theme.palette.semantic["surface/card"] },
  },
  profile: { display: "flex", alignItems: "center", gap: 16 },
  certified: {
    gap: 8,
    "& $avatar": { border: `4px solid ${theme.palette.semantic["surface/card"]}`, boxSizing: "content-box" },
    "& $name": { fontSize: 20, lineHeight: 1.5, [theme.breakpoints.up("md")]: { fontSize: 24 } },
    "& $company": { fontSize: 14 },
    "& $strengths": { marginTop: 0 },
    [theme.breakpoints.up("md")]: { backgroundColor: theme.palette.semantic["surface/subtle"] },
  },
  avatar: { width: 96, height: 96, borderRadius: "50%", objectFit: "cover", flexShrink: 0 },
  names: { display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 },
  name: { fontSize: 16, fontWeight: 700, lineHeight: 1.3, [theme.breakpoints.up("md")]: { fontSize: 24 } },
  company: { fontSize: 12, lineHeight: 1.5, color: theme.palette.text.secondary, [theme.breakpoints.up("md")]: { fontSize: 14 } },
  // 「対応可能種別」の行。Figma B-1 は見出しとタグの間が 16px
  types: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16, marginTop: 4 },
  label: { fontSize: 12, lineHeight: 1.5, color: theme.palette.text.secondary, [theme.breakpoints.up("md")]: { fontSize: 14 } },
  // 「得意な領域」は見出しではなく、種別の行と同じ小さな見出し文字（Figma B-1）
  strengths: { display: "flex", flexDirection: "column", gap: 8, marginTop: 4 },
  message: {
    "& p": {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: 1.5,
      marginTop: 0,
      [theme.breakpoints.up("md")]: { fontSize: 16 },
    },
  },
  // 名前の上にロゴと会社HPを並べ、狭い端末では折り返す。
  footer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: 12,
    rowGap: 8,
  },
  logo: { width: 96, height: 24, objectFit: "contain" },
  link: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: theme.palette.primary.main,
    marginLeft: "auto",
  },
}));

// プランナー詳細とメッセージ詳細で同じ基本情報パーツを使う。
// 相性スコアはここには置かず、「あなたとの相性」セクション（AgentCompatibilitySection）だけに出す。
export const AgentDetailHeader: React.FC<{
  agent: Agent;
  variant?: "legacy" | "planner" | "certified";
  showBeginnerMessage?: boolean;
  strengthLabels?: readonly string[];
  nameComponent?: "h1" | "h2";
}> = ({
  agent,
  variant = "legacy",
  showBeginnerMessage = true,
  strengthLabels = [],
  nameComponent,
}) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const isCertified = variant === "certified";
  const strengths = strengthLabels.length > 0 && (
    <Box className={classes.strengths} data-testid="planner-detail-strengths">
      <Typography className={classes.label}>{PLANNER_STRENGTHS_LABEL}</Typography>
      <StrengthTags labels={strengthLabels} />
    </Box>
  );
  if (variant === "legacy")
    return (
      <Box data-testid="planner-detail-information">
        <AgentProfile
          profileImagePath={agent.agent_profile_image?.image_url || ""}
          companyImagePath={agent.company?.company_logo_image?.image_url || ""}
          companyName={agent.company?.name || ""}
          companyHomePageUrl={agent.company?.home_page_url}
          representativeName={`${agent.family_name ?? ""}${agent.first_name ?? ""}`}
          supportedPropertyTypes={buildSupportedPropertyTypeLabels(agent.supported_property_types)}
        />
        {strengths}
        {showBeginnerMessage && <AgentBeginnerMessage message={agent.beginner_message} variant="detail" />}
      </Box>
    );
  return (
    <Box className={`${classes.hero} ${isCertified ? classes.certified : ""}`} data-testid="planner-detail-hero">
      {isCertified && <CertifiedPlannerBadge />}
      <Box className={classes.profile}>
        <img
          className={classes.avatar}
          src={agent.agent_profile_image?.image_url || NoImage}
          alt={buildAgentName(agent)}
        />
        <Box className={classes.names}>
          {(agent.company?.company_logo_image?.image_url || agent.company?.home_page_url) && (
            <Box className={classes.footer} data-testid="planner-detail-company-links">
              {agent.company?.company_logo_image?.image_url && (
                <img
                  className={classes.logo}
                  src={agent.company.company_logo_image.image_url}
                  alt={agent.company.name}
                />
              )}
              {agent.company?.home_page_url && (
                <a
                  href={agent.company.home_page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.link}
                >
                  会社HP
                  <Chevron direction="forward" color="primary" />
                </a>
              )}
            </Box>
          )}
          <Typography component={nameComponent ?? (isMobile ? "h2" : "h1")} className={classes.name}>
            {buildAgentName(agent)}
          </Typography>
          <Typography className={classes.company}>{agent.company?.name}</Typography>
          <Box className={classes.types}>
            <Typography className={classes.label}>{PLANNER_SUPPORTED_TYPES_LABEL}</Typography>
            <PropertyTypeTags labels={buildSupportedPropertyTypeLabels(agent.supported_property_types)} />
          </Box>
        </Box>
      </Box>
      {strengths}
      {showBeginnerMessage && (
        <Box className={classes.message}>
          <AgentBeginnerMessage message={agent.beginner_message} variant="detail" />
        </Box>
      )}
    </Box>
  );
};
